/* Enhance server pagination without changing its product order or filter state. */
(() => {
  'use strict';
  if (customElements.get('collection-load-more')) return;

  class CollectionLoadMore extends HTMLElement {
    constructor() {
      super();
      this._loading = false;
      this._generation = 0;
      this._onClick = (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        this._loadNextPage();
      };
    }

    connectedCallback() {
      this._generation += 1;
      this._loading = false;
      this._sectionId = this.dataset.sectionId || '';
      this._nextUrl = this.dataset.nextUrl || '';
      this._totalItems = Number(this.dataset.totalItems) || 0;
      this._perPage = Number(this.dataset.perPage) || 24;
      this._hasNext = this.dataset.hasNext === 'true';
      const collection = this.closest('.collection');
      const container = collection?.matches('#product-grid') ? collection : collection?.querySelector('#product-grid');
      this._grid = container?.querySelector(':scope > ul.product-grid') || container;
      if (!this._grid || !this._sectionId || !this._nextUrl) return;

      this._currentLoaded = this._grid.querySelectorAll(':scope > .grid__item').length;
      this._seenProducts = new Set();
      this._seenPages = new Set();
      this._readVisibleProducts();
      this._buildDOM();
      this._btn.addEventListener('click', this._onClick);
      this._pagination = [...collection.querySelectorAll('.pagination-wrapper')];
      this._pagination.forEach(element => { element.hidden = true; });
      this.classList.add('is-initialized');
      this._updateStatus();
      this._setLoadingState(false);
    }

    disconnectedCallback() {
      this._generation += 1;
      this._requestAbort?.abort();
      this._btn?.removeEventListener('click', this._onClick);
    }

    _productKey(item) {
      const link = item.querySelector('.card__heading a, .dut-journal-card h2 a');
      if (!link) return '';
      return new URL(link.getAttribute('href'), location.origin).pathname;
    }

    _readVisibleProducts() {
      this._grid.querySelectorAll(':scope > .grid__item').forEach(item => {
        const key = this._productKey(item);
        if (key) this._seenProducts.add(key);
      });
    }

    _buildDOM() {
      this.innerHTML = '<div class="collection-load-more__status" aria-live="polite" role="status"></div>' +
        '<div class="collection-load-more__progress" role="progressbar" aria-valuemin="0" aria-valuenow="0">' +
        '<span class="collection-load-more__progress-fill"></span></div>' +
        '<a class="collection-load-more__button"><span class="btn-text">Load More</span>' +
        '<span class="btn-loading-text" aria-hidden="true">Loading…</span></a>';
      this._btn = this.querySelector('.collection-load-more__button');
      this._statusEl = this.querySelector('.collection-load-more__status');
      this._progressEl = this.querySelector('.collection-load-more__progress');
      this._progressFill = this.querySelector('.collection-load-more__progress-fill');
      this._btn.setAttribute('aria-label', this.dataset.itemLabel === 'article' ? 'Load more articles' : 'Load more products');
    }

    async _fetchPage(nextUrl) {
      const url = new URL(nextUrl, location.origin);
      url.searchParams.set('section_id', this._sectionId);
      url.searchParams.sort();
      if (url.origin !== location.origin || this._seenPages.has(url.href)) throw new Error('Pagination did not advance');
      const request = new AbortController();
      this._requestAbort = request;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; request.abort(); }, 10000);
      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error('Products unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const container = doc.getElementById('product-grid');
        const grid = container?.querySelector(':scope > ul.product-grid') || container;
        if (!grid || (!grid.matches('ul') && !grid.matches('.collection--empty'))) throw new Error('Product grid missing');
        const items = Array.from(grid.querySelectorAll(':scope > .grid__item'));
        const pagination = doc.querySelector('collection-load-more');
        const next = pagination?.dataset.hasNext === 'true' ? pagination.dataset.nextUrl : '';
        if (next) {
          const nextPage = new URL(next, location.origin);
          nextPage.searchParams.set('section_id', this._sectionId);
          nextPage.searchParams.sort();
          if (nextPage.href === url.href || this._seenPages.has(nextPage.href)) throw new Error('Pagination did not advance');
        }
        this._seenPages.add(url.href);
        return { items, nextUrl: next || '' };
      } catch (error) {
        if (timedOut) throw new Error('Products request timed out');
        throw error;
      } finally {
        clearTimeout(timeout);
        if (this._requestAbort === request) this._requestAbort = null;
      }
    }

    async _loadNextPage() {
      if (this._loading || !this._hasNext) return;
      const generation = this._generation;
      this._loading = true;
      this._setLoadingState(true);
      this._grid.setAttribute('aria-busy', 'true');
      try {
        const page = await this._fetchPage(this._nextUrl);
        if (!this.isConnected || generation !== this._generation) return;
        const items = page.items.filter(item => {
          const key = this._productKey(item);
          if (key && this._seenProducts.has(key)) return false;
          if (key) this._seenProducts.add(key);
          return true;
        });
        this._appendProducts(items);
        this._nextUrl = page.nextUrl;
        this._hasNext = Boolean(this._nextUrl);
        this.dataset.nextUrl = this._nextUrl;
        this.dataset.hasNext = String(this._hasNext);
        this._btn.querySelector('.btn-text').textContent = 'Load More';
        this._updateStatus();
      } catch (error) {
        if (this.isConnected && generation === this._generation && error.name !== 'AbortError') {
          this._pagination.forEach(element => { element.hidden = false; });
          this._statusEl.textContent = 'Couldn’t load products. Try again or use the page links.';
          this._btn.querySelector('.btn-text').textContent = 'Try again';
        }
      } finally {
        if (this.isConnected && generation === this._generation) {
          this._loading = false;
          this._setLoadingState(false);
          this._grid.removeAttribute('aria-busy');
        }
      }
    }

    _appendProducts(items) {
      items.forEach(item => {
        item.classList.remove('scroll-trigger--offscreen');
        item.classList.add('scroll-trigger--cancel');
        this._grid.append(item);
      });
      this._currentLoaded += items.length;
    }

    _updateStatus() {
      const label = this.dataset.itemLabel === 'article' ? 'article' : 'product';
      const total = this._totalItems;
      const viewed = this._currentLoaded;
      if (this.dataset.totalCapped === 'true') {
        this._statusEl.innerHTML = `You've viewed <strong>${viewed}</strong> ${label}${viewed === 1 ? '' : 's'}`;
        this._progressEl.hidden = true;
        return;
      }
      this._statusEl.innerHTML = !this._hasNext && viewed >= total
        ? `You've viewed all <strong>${total}</strong> ${label}${total === 1 ? '' : 's'}`
        : `You've viewed <strong>${viewed}</strong> of <strong>${total}</strong> ${label}${total === 1 ? '' : 's'}`;
      this._progressEl.setAttribute('aria-valuemax', String(total));
      this._progressEl.setAttribute('aria-valuenow', String(viewed));
      const progress = `${total ? Math.min(100, viewed / total * 100) : 0}%`;
      this._progressFill.style.setProperty('--load-more-progress', progress);
      this._progressFill.style.width = progress;
    }

    _setLoadingState(loading) {
      this._btn.classList.toggle('is-loading', loading);
      this._btn.setAttribute('aria-disabled', String(loading || !this._hasNext));
      if (this._hasNext) this._btn.href = this._nextUrl;
      else this._btn.removeAttribute('href');
      this._btn.hidden = !this._hasNext;
      this._btn.style.display = this._hasNext ? '' : 'none';
      if (loading) this._btn.setAttribute('aria-busy', 'true');
      else this._btn.removeAttribute('aria-busy');
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);
})();
